/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Launch } from '@carbon/react/icons';
import { Link } from 'carbon-components-react';
import classnames from 'classnames';
import { string, shape, node, func, arrayOf } from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';

import { Bar } from './Bar';
import { messages } from './messages';

import './SkillConnectionInformationHeader.scss';

/**
 * A Component for rendering search config information header which shows instance details and
 * project details.
 */
export const SkillConnectionInformationHeader = ({
  information,
  className
}) => {
  const { formatMessage: t } = useIntl();

  return (
    <Bar
      className={classnames('Skill__connection-information-header', className)}>
      <div className="connection-information-container">
        {information.title && <h3>{information.title}</h3>}
        <ul className="information-list">
          {information.list.map((item, i) => (
            <li key={`information-list-${i}-${item.title}`}>
              <h4>{item.title}</h4>
              <div>{item.value}</div>
            </li>
          ))}
        </ul>
      </div>
      <ul className="connection-actions-container">
        {information.actions.map((action, i) => (
          <li key={`information-action-${i}`}>
            <Link
              target="_blank"
              rel="noopener noreferrer"
              onClick={action.onClick}
              href={action.href}
              disabled={(!action.href && !action.onClick) || action.disabled}>
              <Launch
                size={16}
                className="bx--btn__icon"
                aria-label={t(messages.openInDiscovery)}
              />
            </Link>
          </li>
        ))}
      </ul>
    </Bar>
  );
};

SkillConnectionInformationHeader.propTypes = {
  className: string,
  information: shape({
    title: string,
    list: arrayOf(
      shape({
        title: string,
        value: node
      })
    ).isRequired,
    actions: arrayOf(
      shape({
        type: string,
        text: node,
        href: string,
        onClick: func
      })
    ).isRequired
  })
};

export default SkillConnectionInformationHeader;
